import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card,
  Form,
  Input,
  Button,
  Typography,
  Space,
  Checkbox,
  Spin,
  Tag,
  message,
  Row,
  Col,
} from 'antd';
import {
  HomeOutlined,
  ShopOutlined,
  FileTextOutlined,
  CheckCircleFilled,
  StarFilled,
} from '@ant-design/icons';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { dealerService, DealerPackage } from '../api/services/dealerService';
import { authService } from '../api/services/authService';
import { useAuthContext } from '../context/AuthContext';
import DealerWelcome from './components/DealerWelcome';
import MainHeader from '../header/MainHeader';
import Footer from '../footer/Footer';
import './DealerRegister.css';
import '../auth.css';

const { Title, Text, Paragraph } = Typography;

const STEPS = [
  { titleKey: 'dealer_register.step_company',  icon: HomeOutlined },
  { titleKey: 'dealer_register.step_package',  icon: ShopOutlined },
  { titleKey: 'dealer_register.step_legal',    icon: FileTextOutlined },
];

const StepBar: React.FC<{ current: number }> = ({ current }) => {
  const { t } = useTranslation();
  return (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    {STEPS.map((step, idx) => {
      const Icon = step.icon;
      const done = idx < current;
      const active = idx === current;
      return (
        <React.Fragment key={idx}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{
              width: 36, height: 36, borderRadius: '50%',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: active ? ORANGE : done ? '#fff' : '#f5f5f5',
              border: active ? `2px solid ${ORANGE}` : done ? `2px solid ${ORANGE}` : '2px solid #e0e0e0',
              color: active ? '#fff' : done ? ORANGE : '#bbb',
              fontSize: 16, flexShrink: 0, transition: 'all 0.2s',
            }}>
              <Icon />
            </div>
            <span style={{
              fontSize: 13, fontWeight: active ? 600 : 400,
              color: active ? ORANGE : done ? '#555' : '#bbb',
              whiteSpace: 'nowrap',
            }}>
              {t(step.titleKey)}
            </span>
          </div>
          {idx < STEPS.length - 1 && (
            <div style={{
              width: 40, height: 2, margin: '0 8px', flexShrink: 0,
              background: done ? ORANGE : '#e0e0e0',
              transition: 'background 0.3s',
            }} />
          )}
        </React.Fragment>
      );
    })}
  </div>
  );
};

const ORANGE = '#fe5a1d';

const DealerRegister: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { updateAuth } = useAuthContext();
  const dealerIdFromUrl = searchParams.get('dealerId');

  const [showWelcome, setShowWelcome] = useState(true);
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [dealerId, setDealerId] = useState<string | null>(dealerIdFromUrl || localStorage.getItem('dealerId'));
  const [packages, setPackages] = useState<DealerPackage[]>([]);
  const [selectedPackage, setSelectedPackage] = useState<DealerPackage | null>(null);

  const [form1] = Form.useForm();
  const [form3] = Form.useForm();

  // Load packages
  useEffect(() => {
    dealerService.getDealerPackages().then(setPackages).catch(() => {
      message.error(t('dealer_register.failed_packages'));
    });
  }, []);

  // Load existing registration
  useEffect(() => {
    if (dealerIdFromUrl && !showWelcome) {
      setLoading(true);
      dealerService.getDealerData(dealerIdFromUrl)
        .then(data => {
          setDealerId(dealerIdFromUrl);
          if (data.data?.companyName) form1.setFieldsValue(data.data);
        })
        .catch(() => message.error(t('dealer_register.failed_load_registration')))
        .finally(() => setLoading(false));
    }
  }, [dealerIdFromUrl, showWelcome]);

  const handleStep1 = async (values: any) => {
    setLoading(true);
    try {
      if (dealerId) {
        await dealerService.updateDealerRegistration(dealerId, { ...values, step: 1 });
      } else {
        const res = await dealerService.createDealerRegistration({ ...values, step: 1 });
        const newId = res.dealerId;
        setDealerId(newId);
        localStorage.setItem('dealerId', newId);
      }
      setCurrentStep(1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e: any) {
      message.error(e?.response?.data?.message || t('dealer_register.failed_save'));
    } finally {
      setLoading(false);
    }
  };

  const handleStep2 = async () => {
    if (!selectedPackage) {
      message.warning(t('dealer_register.select_package_warning'));
      return;
    }
    if (!dealerId) return;
    setLoading(true);
    try {
      await dealerService.updateDealerRegistration(dealerId, {
        dealerPackageId: selectedPackage.id,
        step: 2,
      });
      setCurrentStep(2);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e: any) {
      message.error(e?.response?.data?.message || t('dealer_register.failed_package'));
    } finally {
      setLoading(false);
    }
  };

  const handleSkipStep2 = () => {
    setCurrentStep(2);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleStep3 = async (values: any) => {
    if (!dealerId) return;
    if (values.dealerPassword !== values.confirmPassword) {
      form3.setFields([{ name: 'confirmPassword', errors: [t('dealer_register.passwords_no_match')] }]);
      return;
    }
    setLoading(true);
    try {
      await dealerService.updateDealerRegistration(dealerId, {
        termsAccepted: values.termsAccepted,
        privacyAccepted: values.privacyAccepted,
        businessRegistration: values.businessRegistration,
        registrationCode: values.registrationCode,
        dealerEmail: values.dealerEmail,
        dealerPassword: values.dealerPassword,
        step: 3,
      });
      await dealerService.submitDealerRegistration(dealerId);
      // Auto-login and update app auth state
      const authResult = await authService.login({ email: values.dealerEmail, password: values.dealerPassword });
      const token = (authResult as any).access_token || authResult.token;
      if (token && authResult.user) updateAuth(token, authResult.user);
      localStorage.removeItem('dealerId');
      navigate('/dashboard/subscription');
    } catch (e: any) {
      message.error(e?.response?.data?.message || t('dealer_register.failed_credentials'));
    } finally {
      setLoading(false);
    }
  };

  if (loading && !packages.length) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (showWelcome) {
    return <DealerWelcome onStartRegistration={() => { setShowWelcome(false); window.scrollTo({ top: 0 }); }} />;
  }

  return (
    <div className="auth-page-light home-page-light">
      <MainHeader />
      <main className="auth-main">
        <div className="auth-visual-bg" />
        <div style={{ maxWidth: 1100, margin: '120px auto 40px', padding: '0 24px', position: 'relative', zIndex: 10 }}>

          {/* Stepper */}
          <Card style={{ borderRadius: 16, marginBottom: 24, position: 'sticky', top: 80, zIndex: 1000, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
            <StepBar current={currentStep} />
          </Card>

          {/* Step Content */}
          <Card style={{ borderRadius: 16, boxShadow: '0 20px 40px rgba(0,0,0,0.1)' }}>
            <Spin spinning={loading}>

              {/* ─── Step 1: Company Info ─── */}
              {currentStep === 0 && (
                <div>
                  <Title level={3}>{t('dealer_register.company_title')}</Title>
                  <Text type="secondary">{t('dealer_register.company_subtitle')}</Text>
                  <Form form={form1} layout="vertical" onFinish={handleStep1} style={{ marginTop: 24 }}>
                    <Form.Item name="companyName" label={t('dealer_register.company_name')} rules={[{ required: true }]}>
                      <Input placeholder={t('dealer_register.ph_company_name')} size="large" />
                    </Form.Item>
                    <Row gutter={16}>
                      <Col span={16}>
                        <Form.Item name="street" label={t('dealer_register.street')} rules={[{ required: true }]}>
                          <Input placeholder={t('dealer_register.ph_street')} size="large" />
                        </Form.Item>
                      </Col>
                      <Col span={8}>
                        <Form.Item name="streetNumber" label={t('dealer_register.number')} rules={[{ required: true }]}>
                          <Input placeholder={t('dealer_register.ph_number')} size="large" />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row gutter={16}>
                      <Col span={8}>
                        <Form.Item name="zipCode" label={t('dealer_register.zip')} rules={[{ required: true }]}>
                          <Input placeholder={t('dealer_register.ph_zip')} size="large" />
                        </Form.Item>
                      </Col>
                      <Col span={16}>
                        <Form.Item name="city" label={t('dealer_register.city')} rules={[{ required: true }]}>
                          <Input placeholder={t('dealer_register.ph_city')} size="large" />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Form.Item name="phone" label={t('dealer_register.phone')} rules={[{ required: true }]}>
                      <Input addonBefore="+355" placeholder={t('dealer_register.ph_phone')} size="large" />
                    </Form.Item>
                    <Form.Item name="email" label={t('dealer_register.email')} rules={[{ required: true, type: 'email' }]}>
                      <Input placeholder="info@yourdealership.al" size="large" />
                    </Form.Item>
                    <Form.Item name="additionalAddress" label={t('dealer_register.additional')}>
                      <Input placeholder={t('dealer_register.ph_additional')} size="large" />
                    </Form.Item>
                    <Button type="primary" htmlType="submit" size="large" style={{ background: ORANGE, borderColor: ORANGE, width: '100%', marginTop: 8 }}>
                      {t('dealer_register.continue_to_packages')}
                    </Button>
                  </Form>
                </div>
              )}

              {/* ─── Step 2: Package Selection ─── */}
              {currentStep === 1 && (
                <div>
                  <div style={{ marginBottom: 28 }}>
                    <Title level={3} style={{ marginBottom: 4 }}>{t('dealer_register.choose_package')}</Title>
                    <Text type="secondary">{t('dealer_register.choose_subtitle')}</Text>
                    <div style={{ marginTop: 12, padding: '10px 14px', background: '#fff8f0', borderRadius: 8, border: '1px solid #ffe4cc', display: 'flex', alignItems: 'center', gap: 8 }}>
                      <ShopOutlined style={{ color: ORANGE, fontSize: 15, flexShrink: 0 }} />
                      <Text style={{ fontSize: 13, color: '#666' }}>
                        {t('dealer_register.package_optional_note')}
                      </Text>
                    </div>
                  </div>

                  {packages.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '56px 0' }}>
                      <ShopOutlined style={{ fontSize: 48, color: '#d9d9d9', display: 'block', marginBottom: 16 }} />
                      <Text type="secondary">{t('dealer_register.no_packages')}</Text>
                    </div>
                  ) : (
                    <div className="dealer-pkg-grid" style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(packages.length, 4)}, 1fr)`, gap: 14 }}>
                      {packages.map((pkg, idx) => {
                        const isSelected = selectedPackage?.id === pkg.id;
                        const isPopular = packages.length >= 2 && idx === Math.floor((packages.length - 1) / 2);
                        return (
                          <div
                            key={pkg.id}
                            onClick={() => setSelectedPackage(pkg)}
                            className="dealer-pkg-card"
                            style={{
                              border: isSelected ? `2px solid ${ORANGE}` : isPopular ? `2px solid #ffd4b8` : '2px solid #f0f0f0',
                              borderRadius: 14,
                              cursor: 'pointer',
                              background: isSelected ? '#fff8f5' : '#fff',
                              position: 'relative',
                              overflow: 'hidden',
                              boxShadow: isSelected
                                ? '0 6px 24px rgba(254,90,29,0.14)'
                                : isPopular
                                ? '0 4px 16px rgba(254,90,29,0.08)'
                                : '0 2px 8px rgba(0,0,0,0.04)',
                              transition: 'all 0.2s ease',
                            }}
                          >
                            {/* Popular ribbon */}
                            {isPopular && (
                              <div style={{
                                background: `linear-gradient(90deg, ${ORANGE} 0%, #ff8a50 100%)`,
                                color: '#fff',
                                fontSize: 11,
                                fontWeight: 700,
                                letterSpacing: '0.8px',
                                textTransform: 'uppercase',
                                textAlign: 'center',
                                padding: '6px 0',
                              }}>
                                <StarFilled style={{ marginRight: 5, fontSize: 10 }} />
                                {t('dealer_register.most_popular')}
                              </div>
                            )}

                            <div style={{ padding: '20px 24px' }}>
                              {/* Row: name + selected badge + radio */}
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                  <Text strong style={{ fontSize: 16 }}>{pkg.name}</Text>
                                  {isSelected && (
                                    <Tag color="orange" style={{ margin: 0, fontSize: 11, fontWeight: 600, borderRadius: 20 }}>
                                      ✓ {t('common.confirm')}
                                    </Tag>
                                  )}
                                </div>
                                <div style={{
                                  width: 20,
                                  height: 20,
                                  borderRadius: '50%',
                                  border: isSelected ? `6px solid ${ORANGE}` : '2px solid #d9d9d9',
                                  background: '#fff',
                                  flexShrink: 0,
                                  transition: 'all 0.2s',
                                  boxShadow: isSelected ? `0 0 0 2px rgba(254,90,29,0.15)` : 'none',
                                }} />
                              </div>

                              {/* Price */}
                              <div style={{ marginBottom: 12 }}>
                                <span style={{ fontSize: 30, fontWeight: 800, color: ORANGE, lineHeight: 1 }}>
                                  {Number(pkg.price).toLocaleString()}
                                </span>
                                <span style={{ fontSize: 13, color: '#999', marginLeft: 6, fontWeight: 500 }}>
                                  {pkg.currency} {t('dealer_register.per_month')}
                                </span>
                              </div>

                              {/* Divider */}
                              <div style={{ height: 1, background: '#f0f0f0', marginBottom: 12 }} />

                              {/* Description */}
                              {pkg.description && (
                                <Text type="secondary" style={{ fontSize: 13, lineHeight: '1.5', display: 'block', marginBottom: 10 }}>
                                  {pkg.description}
                                </Text>
                              )}

                              {/* Features */}
                              {pkg.features && pkg.features.length > 0 && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                                  {pkg.features.map((f, i) => (
                                    <span key={i} style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 13, color: '#444' }}>
                                      <CheckCircleFilled style={{ color: '#52c41a', fontSize: 13, flexShrink: 0 }} />
                                      {f}
                                    </span>
                                  ))}
                                </div>
                              )}

                              {/* Max listings */}
                              {pkg.maxListings != null && (
                                <div style={{ marginTop: 12 }}>
                                  <span style={{
                                    background: isSelected ? 'rgba(254,90,29,0.1)' : '#f7f7f7',
                                    borderRadius: 20,
                                    padding: '3px 12px',
                                    fontSize: 12,
                                    color: isSelected ? ORANGE : '#777',
                                    fontWeight: 600,
                                  }}>
                                    {t('dealer_register.listings_max', { count: pkg.maxListings })}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  <Space style={{ marginTop: 28, width: '100%', justifyContent: 'space-between' }} align="center">
                    <Button size="large" onClick={() => setCurrentStep(0)}>{t('common.back')}</Button>
                    <Space>
<Button
                        type="primary"
                        size="large"
                        disabled={!selectedPackage}
                        loading={loading}
                        onClick={handleStep2}
                        style={{ background: ORANGE, borderColor: ORANGE, minWidth: 180 }}
                      >
                        {selectedPackage ? t('dealer_register.continue_with', { name: selectedPackage.name }) : t('dealer_register.select_package')}
                      </Button>
                    </Space>
                  </Space>
                </div>
              )}

              {/* ─── Step 3: Legal & Credentials ─── */}
              {currentStep === 2 && (
                <div>
                  <Title level={3}>{t('dealer_register.legal_title')}</Title>
                  <Text type="secondary">{t('dealer_register.legal_subtitle')}</Text>
                  <Form form={form3} layout="vertical" onFinish={handleStep3} style={{ marginTop: 24 }}>
                    <Form.Item name="termsAccepted" valuePropName="checked" rules={[{ validator: (_, v) => v ? Promise.resolve() : Promise.reject('Required') }]}>
                      <Checkbox>{t('dealer_register.accept_terms').split('Terms & Conditions')[0]}<a href="/terms-and-conditions" target="_blank">Terms & Conditions</a></Checkbox>
                    </Form.Item>
                    <Form.Item name="privacyAccepted" valuePropName="checked" rules={[{ validator: (_, v) => v ? Promise.resolve() : Promise.reject('Required') }]}>
                      <Checkbox>{t('dealer_register.accept_privacy').split('Privacy Policy')[0]}<a href="/privacy-policy" target="_blank">Privacy Policy</a></Checkbox>
                    </Form.Item>
                    <Form.Item name="businessRegistration" valuePropName="checked" rules={[{ validator: (_, v) => v ? Promise.resolve() : Promise.reject('Required') }]}>
                      <Checkbox>{t('dealer_register.confirm_business')}</Checkbox>
                    </Form.Item>
                    <Form.Item name="registrationCode" label={t('dealer_register.nipt')}>
                      <Input placeholder={t('dealer_register.ph_nipt')} size="large" />
                    </Form.Item>
                    <Title level={5} style={{ marginTop: 16 }}>{t('dealer_register.create_account_title')}</Title>
                    <Form.Item name="dealerEmail" label={t('dealer_register.dealer_email')} rules={[{ required: true, type: 'email' }]}>
                      <Input placeholder={t('dealer_register.ph_dealer_email')} size="large" />
                    </Form.Item>
                    <Form.Item name="dealerPassword" label={t('dealer_register.dealer_password')} rules={[{ required: true, min: 8, message: t('dealer_register.min_8_chars') }]}>
                      <Input.Password placeholder={t('dealer_register.ph_password_min')} size="large" />
                    </Form.Item>
                    <Form.Item name="confirmPassword" label={t('dealer_register.confirm_password')} rules={[{ required: true }]}>
                      <Input.Password placeholder={t('dealer_register.ph_password_repeat')} size="large" />
                    </Form.Item>
                    <Space style={{ marginTop: 8, width: '100%', justifyContent: 'space-between' }}>
                      <Button size="large" onClick={() => setCurrentStep(1)}>{t('common.back')}</Button>
                      <Button type="primary" htmlType="submit" size="large" style={{ background: ORANGE, borderColor: ORANGE }}>
                        {t('dealer_register.create_account')}
                      </Button>
                    </Space>
                  </Form>
                </div>
              )}

            </Spin>
          </Card>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default DealerRegister;
